import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

import { BrowserRouter, Routes, Route, Navigate, Link, useNavigate, useLocation } from 'react-router-dom';
import Home from './pages/Home';
import Support from './pages/Support';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import ProjectSettings from './pages/ProjectSettings';
import Requirements from './pages/Requirements';
import RequirementDetails from './pages/RequirementDetails';
import Folders from './pages/Folders';
import TestCases from './pages/TestCases';
import TestCaseDetails from './pages/TestCaseDetails';
import TestSuites from './pages/TestSuites';
import TestSuiteDetail from './pages/TestSuiteDetail';
import TestRuns from './pages/TestRuns';
import TestRunDetail from './pages/TestRunDetail';
import TestResults from './pages/TestResults';
import JenkinsConfig from './pages/JenkinsConfig';
import JiraConfig from './pages/JiraConfig';
import JiraImport from './pages/JiraImport';
import ArchivedItems from './pages/ArchivedItems';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import OAuthCallback from './pages/OAuthCallback';
import Teams from './pages/Teams';
import UserManagement from './pages/UserManagement';
import RolesPermissions from './pages/RolesPermissions';
import Setup from './pages/Setup';
import ChangePassword from './pages/ChangePassword';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ProtectedRoute from './components/ProtectedRoute';
import Sidebar from './components/Sidebar';
import GlobalSidebar from './components/GlobalSidebar';
import UserHome from './pages/UserHome';
import GlobalSettings from './pages/GlobalSettings';

function Navigation({ user, onLogout }) {
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    onLogout();
    navigate('/');
  };

  const initials = user?.username
    ? user.username.slice(0, 2).toUpperCase()
    : '?';

  return (
    <nav className="navbar navbar-expand-lg zq-navbar fixed-top" style={{ height: '60px' }}>
      <div className="container-fluid">
        <Link className="navbar-brand" to="/">
          <span className="brand-logo">Z</span>
          ZipQA
        </Link>
        <button
          className="navbar-toggler border-0"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarNav"
          style={{ color: 'rgba(255,255,255,.7)' }}
        >
          <span className="navbar-toggler-icon"></span>
        </button>
        <div className="collapse navbar-collapse" id="navbarNav">
          {user ? (
            <>
              <ul className="navbar-nav me-auto mb-2 mb-lg-0">
                <li className="nav-item">
                  <Link className="nav-link" to="/home">
                    Home
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link" to="/projects">
                    Projects
                  </Link>
                </li>
              </ul>
              <div className="navbar-user">
                <span className="avatar">{initials}</span>
                <span className="d-none d-md-inline">{user.username}</span>
                <span
                  className="badge"
                  style={{ background: 'rgba(255,255,255,.15)', color: 'rgba(255,255,255,.7)', fontWeight: 400 }}
                >
                  {user.role}
                </span>
                <button className="btn btn-logout btn-sm" onClick={handleLogout}>
                  Sign out
                </button>
              </div>
            </>
          ) : (
            <ul className="navbar-nav ms-auto">
              <li className="nav-item">
                <Link className="nav-link" to="/login">
                  Login
                </Link>
              </li>
            </ul>
          )}
        </div>
      </div>
    </nav>
  );
}

function AppContent({ user, onLogin }) {
  const location = useLocation();
  
  // Extract projectKey from various route patterns (path params and query params)
  const getProjectKeyFromPath = () => {
    // First try to get from URL path
    const pathMatch = location.pathname.match(/\/projects\/([^/]+)/);
    if (pathMatch) {
      return pathMatch[1];
    }
    
    // Then try to get from query parameters
    const searchParams = new URLSearchParams(location.search);
    const projectKey = searchParams.get('projectKey');
    if (projectKey) {
      return projectKey;
    }
    
    return null;
  };

  const projectKey = getProjectKeyFromPath();
  const isHomePage = location.pathname === '/' || location.pathname === '/support';

  // Show project sidebar only when inside a project route
  const showProjectSidebar = user && projectKey;
  // Show global sidebar on all authenticated non-project pages (except public home)
  const showGlobalSidebar = user && !projectKey && !isHomePage;
  const showSidebar = showProjectSidebar || showGlobalSidebar;

  return (
    <div className={`app-container ${!isHomePage ? 'with-navbar-margin' : ''}`}>
      {showGlobalSidebar && <GlobalSidebar />}
      {showProjectSidebar && <Sidebar projectKey={projectKey} />}
      <div className={`main-content ${showSidebar ? 'with-sidebar' : ''} ${isHomePage ? 'home-page-content' : ''}`}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/support" element={<Support />} />
          <Route path="/login" element={<Login onLogin={onLogin} />} />
          <Route path="/setup" element={<Navigate to="/home" replace />} />
          <Route path="/home" element={<ProtectedRoute><UserHome /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><GlobalSettings /></ProtectedRoute>} />
          <Route path="/teams" element={<ProtectedRoute><Teams /></ProtectedRoute>} />
          <Route path="/settings/users" element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
          <Route path="/settings/roles" element={<ProtectedRoute><RolesPermissions /></ProtectedRoute>} />
          <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/oauth-callback" element={<OAuthCallback onLogin={onLogin} />} />
          <Route
            path="/projects"
            element={
              <ProtectedRoute>
                <Projects />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:projectKey"
            element={
              <ProtectedRoute>
                <ProjectDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:projectKey/settings"
            element={
              <ProtectedRoute>
                <ProjectSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:projectKey/requirements"
            element={
              <ProtectedRoute>
                <Requirements />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:projectKey/requirements/:requirementId"
            element={
              <ProtectedRoute>
                <RequirementDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:projectKey/testcases"
            element={
              <ProtectedRoute>
                <TestCases />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:projectKey/testcases/:testCaseId"
            element={
              <ProtectedRoute>
                <TestCaseDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:projectKey/test-suites"
            element={
              <ProtectedRoute>
                <TestSuites />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:projectKey/test-suites/:suiteId"
            element={
              <ProtectedRoute>
                <TestSuiteDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:projectKey/runs"
            element={
              <ProtectedRoute>
                <TestRuns />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:projectKey/results"
            element={
              <ProtectedRoute>
                <TestResults />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:projectKey/runs/:runId"
            element={
              <ProtectedRoute>
                <TestRunDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:projectKey/runs/:runId/results"
            element={
              <ProtectedRoute>
                <TestResults />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:projectKey/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:projectKey/folders/:folderType"
            element={
              <ProtectedRoute>
                <Folders />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:projectKey/archived"
            element={
              <ProtectedRoute>
                <ArchivedItems />
              </ProtectedRoute>
            }
          />
          <Route
            path="/testcases"
            element={
              <ProtectedRoute>
                <TestCases />
              </ProtectedRoute>
            }
          />
          <Route
            path="/suites"
            element={
              <ProtectedRoute>
                <TestSuites />
              </ProtectedRoute>
            }
          />
          <Route
            path="/runs"
            element={
              <ProtectedRoute>
                <TestRuns />
              </ProtectedRoute>
            }
          />
          <Route
            path="/results"
            element={
              <ProtectedRoute>
                <TestResults />
              </ProtectedRoute>
            }
          />
          <Route
            path="/runs/:runId"
            element={
              <ProtectedRoute>
                <TestRunDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/runs/:runId/results"
            element={
              <ProtectedRoute>
                <TestResults />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jenkins"
            element={
              <ProtectedRoute>
                <JenkinsConfig />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jira"
            element={
              <ProtectedRoute>
                <JiraConfig />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jira/import"
            element={
              <ProtectedRoute>
                <JiraImport />
              </ProtectedRoute>
            }
          />
        </Routes>
      </div>
    </div>
  );
}

const API_URL = process.env.REACT_APP_API_URL || '/api';

function App() {
  const [user, setUser]           = useState(null);
  const [needsSetup, setNeedsSetup] = useState(null); // null = checking

  useEffect(() => {
    // Check if user is logged in
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try { setUser(JSON.parse(storedUser)); } catch (e) {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
    }
    // Check whether initial setup is required
    fetch(`${API_URL}/auth/setup-status`)
      .then(r => r.json())
      .then(d => setNeedsSetup(d.needsSetup === true))
      .catch(() => setNeedsSetup(false)); // if backend unreachable, don't block
  }, []);

  const handleLogin = userData => setUser(userData);
  const handleLogout = () => setUser(null);

  // While checking setup status, show a minimal spinner
  if (needsSetup === null) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a' }}>
      <div className="spinner-border text-light" role="status"><span className="visually-hidden">Loading…</span></div>
    </div>
  );

  return (
    <BrowserRouter>
      {needsSetup
        ? (
          // No users at all — force setup wizard, block all other routes
          <Routes>
            <Route path="/setup" element={<Setup onLogin={u => { handleLogin(u); setNeedsSetup(false); }} />} />
            <Route path="*" element={<Navigate to="/setup" replace />} />
          </Routes>
        ) : (
          <>
            <Navigation user={user} onLogout={handleLogout} />
            <AppContent user={user} onLogin={handleLogin} />
          </>
        )
      }
    </BrowserRouter>
  );
}

export default App;
